// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import {
  Box, Button, ButtonVariant, Icon, IconName, Tooltip,
} from '@usealloy/component-library';
import {
  selectDataRoutes,
  selectFocusedAction,
} from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';
import { generatePublicId } from 'flow/service/publicIds';
import { getDynamicDataRoutesForAction } from '~/dashboard/views/workflows/service/dataRoutes';
import DynamicRouteRow from './DynamicRouteRow';
import { StyledDynamicRoutesInfoBanner, StyledDynamicRoutesInfoBannerText } from './styles';

const LEGACY_ROUTES_ACTIVE_TOOLTIP = 'Deactivate the current data routes before adding a new one.';

interface DynamicRoutesProps {
  // Adding a dynamic route makes Flow.js bypass legacy routing for the node, breaking live legacy routes.
  hasActiveLegacyDataRoutes?: boolean;
  isDisabled?: boolean;
}

export default function DynamicRoutes({
  isDisabled = false,
  hasActiveLegacyDataRoutes = false,
}: DynamicRoutesProps) {
  const dataRoutes = useAppSelector(selectDataRoutes);
  const focusedAction = useAppSelector(selectFocusedAction);
  const [pendingNewRouteIds, setPendingNewRouteIds] = useState<string[]>([]);

  const dynamicRoutes = useMemo(() => (
    getDynamicDataRoutesForAction({ action: focusedAction, dataRoutes })
  ), [focusedAction, dataRoutes]);

  const onAddRoute = () => {
    setPendingNewRouteIds(current => [...current, generatePublicId('dataroute')]);
  };

  const onRowSettled = (publicId: string) => {
    setPendingNewRouteIds(current => current.filter(id => id !== publicId));
  };

  const isAddDisabled = pendingNewRouteIds.length > 0 || hasActiveLegacyDataRoutes;
  const addRouteButton = (
    <Button
      variant={ButtonVariant.secondary}
      icon={IconName.Add}
      disabled={isAddDisabled || isDisabled}
      onClick={onAddRoute}
    >
      Add Data Route
    </Button>
  );

  return (
    <Box direction="column" alignItems="stretch" gap={3}>
      <StyledDynamicRoutesInfoBanner>
        <Icon icon={IconName.Info} size="small" />
        <StyledDynamicRoutesInfoBannerText as="span">
          Data routing takes the output of one service or input/output attribute and feeds it
          into the request payload of a different service within the same Workflow.
        </StyledDynamicRoutesInfoBannerText>
      </StyledDynamicRoutesInfoBanner>

      <Box justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
        {/* A disabled Button fires no hover events, so the Tooltip wraps this span instead. */}
        <Tooltip content={LEGACY_ROUTES_ACTIVE_TOOLTIP} hide={!hasActiveLegacyDataRoutes}>
          <span>{addRouteButton}</span>
        </Tooltip>
      </Box>

      {dynamicRoutes.map(dataRoute => (
        <DynamicRouteRow
          key={dataRoute.publicId}
          publicId={dataRoute.publicId}
          onSaved={onRowSettled}
          onRemoved={onRowSettled}
          isDisabled={isDisabled}
        />
      ))}
      {pendingNewRouteIds.map(publicId => (
        <DynamicRouteRow
          key={publicId}
          publicId={publicId}
          onSaved={onRowSettled}
          onRemoved={onRowSettled}
          isDisabled={isDisabled}
        />
      ))}
    </Box>
  );
}
